// Sale detail view. Accessed from the "Última venta" chip or from the history
// list. The "Volver" action is contextual: it returns to wherever the user
// came from (falls back to the history list for deep links). Also supports
// backfilling the net received amount for sales confirmed with
// `skipNetReceived: true`, and correcting a net already loaded while the
// sale is still active.

import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { saleKeys, useSale, useVoidSale } from "./hooks";
import { Card } from "../../components/Card";
import { Chip } from "../../components/Chip";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Sheet } from "../../components/Sheet";
import { PageHeader } from "../../components/PageHeader";
import { MoneyInput } from "../../components/MoneyInput";
import { MoneyConfirmationPanel } from "../../components/MoneyConfirmationPanel";
import { formatARS, getArsAmountPolicy, inputValueFromCents, parseArsInput } from "../../lib/money";
import { prorateSettlementNet } from "../../lib/settlement-money";
import { ApiError } from "../../lib/api";
import { salesApi } from "./api";
import { ProductThumbnail } from "../../components/CatalogVisuals";
import { InfoCircleIcon, PencilIcon } from "../../components/icons";

type SaleDetailLocationState = { from?: string };

// Target payment for the net-received sheet: which payment it edits (id),
// its currently loaded net (null when still pending) and the payment
// method's name, used to label the sheet on split-payment sales so the
// operator knows which one they're correcting.
type NetSheetTarget = {
  paymentId: number;
  currentCents: number | null;
  methodName: string;
};

export function SaleDetailView() {
  const { id } = useParams<{ id: string }>();
  const saleId = id ? Number(id) : null;
  const saleQ = useSale(saleId);
  const voidSale = useVoidSale();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const backTo = (location.state as SaleDetailLocationState | null)?.from ?? "/sales";

  const [netSheetTarget, setNetSheetTarget] = useState<NetSheetTarget | null>(null);
  const [voidConfirmOpen, setVoidConfirmOpen] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [voidError, setVoidError] = useState<string | null>(null);
  const [netSubmitting, setNetSubmitting] = useState(false);

  const sale = saleQ.data;

  const saveNetReceived = async (
    netReceivedCents: number,
    paymentId: number,
    moneyConfirmationToken?: string
  ) => {
    if (saleId == null) throw new Error("No se encontró la venta.");
    setNetSubmitting(true);
    try {
      const updatedSale = await salesApi.setNetReceived(
        saleId,
        netReceivedCents,
        paymentId,
        moneyConfirmationToken
      );
      queryClient.setQueryData(saleKeys.detail(updatedSale.id), updatedSale);
      queryClient.invalidateQueries({ queryKey: saleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ["summary"] });
    } finally {
      setNetSubmitting(false);
    }
  };

  // If sale is voided, the net sheet should not be available.
  useEffect(() => {
    if (sale?.status === "voided") setNetSheetTarget(null);
  }, [sale?.status]);

  if (saleQ.isLoading) {
    return (
      <main style={centerStyle}>
        <PageHeader
          title="Detalle de venta"
          backHref={backTo}
        />
        <p style={{ padding: "var(--space-4)", color: "var(--text-secondary)" }}>
          Cargando...
        </p>
      </main>
    );
  }

  if (!sale) {
    return (
      <main style={{ display: "flex", flexDirection: "column", minHeight: "100dvh" }}>
        <PageHeader
          title="Detalle de venta"
          backHref={backTo}
        />
        <p style={{ padding: "var(--space-4)" }}>No se encontró la venta.</p>
      </main>
    );
  }

  const isPendingNet =
    sale.status === "active" &&
    sale.payments.some((payment) => payment.paymentMethodSnapshot.requiresNetReceived && payment.netReceivedCents === null);

  // Un pago ya incluido en una liquidación activa bloquea la anulación de la
  // venta (lo aplica el backend: 400 sale_in_active_settlement). Se detecta
  // acá para no ofrecer el flujo de anular y explicar el motivo de entrada,
  // sin depender únicamente de que falle la request.
  const blockingSettlement = sale.payments.find(
    (payment) => payment.settlementId !== null && payment.settlement?.status === "active"
  )?.settlement ?? null;

  const commissionPct =
    sale.netReceivedCents !== null && sale.totalChargedCents > 0
      ? (sale.commissionCents / sale.totalChargedCents) * 100
      : null;
  const grossItemsCents = sale.itemsSubtotalCents + sale.lineDiscountTotalCents;
  const paymentDiscountEligibleCents = sale.items.reduce(
    (total, item) => item.lineDiscountCents === 0
      ? total + item.qty * item.unitPriceCents
      : total,
    0
  );
  const paymentDiscountConfigured =
    sale.payments.length === 1 && sale.paymentMethodSnapshot.discountPct > 0;

  return (
    <main className="sale-detail-page">
      <PageHeader
        title="Detalle de venta"
        backHref={backTo}
      />

      <div className="sale-detail-content">
        <Card className={`sale-detail-hero${sale.status === "voided" ? " is-voided" : ""}`}>
          <div className="sale-detail-hero__top">
            <div><p className="eyebrow">Venta #{sale.id}</p><span>Total cobrado</span><strong className="num">{formatARS(sale.totalChargedCents)}</strong></div>
            {sale.status === "voided" ? <Chip variant="tag-danger" size="sm">Anulada</Chip> : <Chip variant="tag-success" size="sm">Activa</Chip>}
          </div>
          <div className="sale-detail-meta-grid">
            <Row label="Fecha" value={new Date(sale.createdAt).toLocaleString("es-AR")} />
            <Row label="Medio" value={sale.paymentMethodSnapshot.name} />
            {sale.customerNameSnapshot && (
              <Row label="Cliente" value={sale.customerNameSnapshot} />
            )}
            <Row label="Operador" value={`#${sale.operatorId}`} />
          </div>
        </Card>

        <Card className="sale-detail-card">
          <div className="sale-detail-section-heading"><div><p className="eyebrow">Detalle</p><strong>Productos</strong></div><span>{sale.items.reduce((total, item) => total + item.qty, 0)} unidades</span></div>
          <div className="sale-detail-items">
            {sale.items.map((it) => (
              <div className="sale-detail-item" key={it.id}>
                <ProductThumbnail productName={it.productName} />
                <span className="sale-detail-item__copy">
                  <strong>{it.qty}× {it.productName}</strong>
                  {it.size && (
                    <span className="sale-detail-size">Talle {it.size}</span>
                  )}
                  {it.lineDiscountCents > 0 && (
                    <small>
                      Descuento propio {Math.round((it.lineDiscountCents / (it.unitPriceCents * it.qty)) * 100)}% · −{formatARS(it.lineDiscountCents)}
                    </small>
                  )}
                  {it.note && <small className="sale-detail-item__note">Nota: {it.note}</small>}
                </span>
                <strong className="num sale-detail-item__total">{formatARS(it.lineSubtotalCents)}</strong>
              </div>
            ))}
          </div>
        </Card>

        {sale.payments.length > 1 && (
          <Card className="sale-detail-card">
            <div className="sale-detail-section-heading"><div><p className="eyebrow">Cobro</p><strong>Pago dividido</strong></div></div>
            <div className="sale-payment-breakdown">
              {sale.payments.map((payment) => {
                const canEditNet = payment.paymentMethodSnapshot.requiresNetReceived && sale.status === "active";
                return (
                  <div key={payment.id}>
                    <span>{payment.paymentMethodSnapshot.name}</span>
                    <strong className="num">{formatARS(payment.amountCents)}</strong>
                    {payment.paymentMethodSnapshot.requiresNetReceived && (
                      <div className="sale-payment-breakdown__net">
                        <small>
                          {payment.netReceivedCents !== null
                            ? `Neto ${formatARS(payment.netReceivedCents)}${payment.commissionCents > 0 ? ` · comisión ${formatARS(payment.commissionCents)}` : ""}`
                            : "Neto pendiente de carga"}
                        </small>
                        {canEditNet && (
                          <button
                            className="icon-action"
                            type="button"
                            aria-label={
                              payment.netReceivedCents !== null
                                ? `Editar neto de ${payment.paymentMethodSnapshot.name}`
                                : `Cargar neto de ${payment.paymentMethodSnapshot.name}`
                            }
                            disabled={netSubmitting}
                            onClick={() =>
                              setNetSheetTarget({
                                paymentId: payment.id,
                                currentCents: payment.netReceivedCents,
                                methodName: payment.paymentMethodSnapshot.name,
                              })
                            }
                          >
                            <PencilIcon />
                          </button>
                        )}
                      </div>
                    )}
                    {/* Cobro por liquidación (ej. ClubCred): nunca pide neto
                        acá, no hay lápiz de edición — el neto real solo
                        existe a nivel de la liquidación, nunca por pago. */}
                    {payment.paymentMethodSnapshot.settlesInBatch && (
                      <div className="sale-payment-breakdown__net">
                        {payment.settlementId === null || !payment.settlement ? (
                          <span className="status-badge status-badge--info">
                            <InfoCircleIcon /> Pendiente de liquidación
                          </span>
                        ) : (
                          <small>
                            Liquidada el {new Date(`${payment.settlement.settledOn}T00:00:00`).toLocaleDateString("es-AR")}
                            {" · "}
                            <Link to={`/liquidaciones/${payment.settlement.id}`}>Ver liquidación</Link>
                          </small>
                        )}
                      </div>
                    )}
                    {payment.settlementId !== null && payment.settlement && payment.settlement.grossCents > 0 && (
                      <small>
                        Neto estimado {formatARS(prorateSettlementNet(payment.amountCents, payment.settlement.grossCents, payment.settlement.transferredCents))}
                        {" "}— prorrateo de la liquidación, estimado
                      </small>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        <Card className="sale-detail-card sale-detail-totals">
          <div className="sale-detail-section-heading"><div><p className="eyebrow">Importes</p><strong>Resumen financiero</strong></div></div>
          <div className="sale-detail-totals__rows">
            <Row label="Total bruto de productos" value={formatARS(grossItemsCents)} />
            {sale.lineDiscountTotalCents > 0 && (
              <Row
                label="Descuentos por línea"
                value={`−${formatARS(sale.lineDiscountTotalCents)}`}
              />
            )}
            {sale.lineDiscountTotalCents > 0 && (
              <Row label="Subtotal después de descuentos propios" value={formatARS(sale.itemsSubtotalCents)} />
            )}
            {sale.globalDiscountCents > 0 && (
              <Row label="Descuento general" value={`−${formatARS(sale.globalDiscountCents)}`} />
            )}
            {sale.paymentMethodDiscountCents > 0 && (
              <Row
                label={`Descuento ${sale.paymentMethodSnapshot.name} (${sale.paymentMethodSnapshot.discountPct}%)`}
                value={`−${formatARS(sale.paymentMethodDiscountCents)}`}
              />
            )}
            <div className="sale-detail-total-final">
              <span>Total cobrado</span>
              <span className="num">{formatARS(sale.totalChargedCents)}</span>
            </div>
          </div>
          {paymentDiscountConfigured && sale.globalDiscountCents === 0 && (
            <div className="sale-discount-explanation">
              <strong>Cómo se calculó el descuento de {sale.paymentMethodSnapshot.name}</strong>
              <span>Base elegible: {formatARS(paymentDiscountEligibleCents)}. Solo incluye productos sin descuento propio.</span>
              {paymentDiscountEligibleCents < grossItemsCents && <small>Los productos que ya tenían descuento quedaron excluidos para no acumular beneficios.</small>}
            </div>
          )}
          {paymentDiscountConfigured && sale.globalDiscountCents > 0 && (
            <div className="sale-discount-explanation"><strong>Descuento del medio no acumulado</strong><span>Se mantuvo el descuento general de la venta y no se sumó el {sale.paymentMethodSnapshot.discountPct}% de {sale.paymentMethodSnapshot.name}.</span></div>
          )}
        </Card>

        {sale.payments.length === 1 && sale.paymentMethodSnapshot.requiresNetReceived && (
          <Card>
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <strong>Neto recibido</strong>
                {!isPendingNet && sale.netReceivedCents !== null && sale.status === "active" && (
                  <button
                    className="icon-action"
                    type="button"
                    aria-label="Editar neto recibido"
                    disabled={netSubmitting}
                    onClick={() =>
                      setNetSheetTarget({
                        paymentId: sale.payments[0].id,
                        currentCents: sale.netReceivedCents,
                        methodName: sale.paymentMethodSnapshot.name,
                      })
                    }
                  >
                    <PencilIcon />
                  </button>
                )}
              </div>
              {sale.netReceivedCents !== null ? (
                <Row label="Neto" value={formatARS(sale.netReceivedCents)} />
              ) : (
                <span style={{ color: "var(--text-secondary)", fontSize: "var(--font-size-14)" }}>
                  Pendiente de carga
                </span>
              )}
              {sale.commissionCents > 0 && (
                <Row
                  label="Comisión"
                  value={
                    commissionPct !== null
                      ? `${formatARS(sale.commissionCents)} (${commissionPct.toFixed(1)}%)`
                      : formatARS(sale.commissionCents)
                  }
                />
              )}
              {isPendingNet && (
                <Button
                  variant="primary"
                  onClick={() =>
                    setNetSheetTarget({
                      paymentId: sale.payments[0].id,
                      currentCents: null,
                      methodName: sale.paymentMethodSnapshot.name,
                    })
                  }
                  disabled={netSubmitting}
                >
                  Cargar neto recibido
                </Button>
              )}
            </div>
          </Card>
        )}

        {sale.payments.length === 1 && sale.paymentMethodSnapshot.settlesInBatch && (
          <Card>
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <strong>Liquidación</strong>
                {sale.payments[0].settlementId === null || !sale.payments[0].settlement ? (
                  <span className="status-badge status-badge--info">
                    <InfoCircleIcon /> Pendiente de liquidación
                  </span>
                ) : (
                  <span className="status-badge status-badge--success">Liquidada</span>
                )}
              </div>
              {sale.payments[0].settlementId === null || !sale.payments[0].settlement ? (
                <span style={{ color: "var(--text-secondary)", fontSize: "var(--font-size-14)" }}>
                  Todavía no se incluyó en ninguna liquidación. La plata llega consolidada más adelante.
                </span>
              ) : (
                <>
                  <Row
                    label="Liquidada el"
                    value={new Date(`${sale.payments[0].settlement.settledOn}T00:00:00`).toLocaleDateString("es-AR")}
                  />
                  <Link to={`/liquidaciones/${sale.payments[0].settlement.id}`} className="text-action">
                    Ver liquidación
                  </Link>
                  {sale.payments[0].settlement.grossCents > 0 && (
                    <span style={{ color: "var(--text-secondary)", fontSize: "var(--font-size-12)" }}>
                      Neto estimado{" "}
                      {formatARS(
                        prorateSettlementNet(
                          sale.payments[0].amountCents,
                          sale.payments[0].settlement.grossCents,
                          sale.payments[0].settlement.transferredCents
                        )
                      )}{" "}
                      — prorrateo de la liquidación, estimado
                    </span>
                  )}
                </>
              )}
            </div>
          </Card>
        )}

        {blockingSettlement && sale.status === "active" && (
          <Card style={{ background: "var(--surface-sunken)" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
                <InfoCircleIcon />
                <strong>No se puede anular</strong>
              </div>
              <span style={{ color: "var(--text-secondary)", fontSize: "var(--font-size-14)" }}>
                Esta venta tiene un pago ya incluido en una liquidación. Para anularla, primero revertí esa liquidación.
              </span>
              <Link to={`/liquidaciones/${blockingSettlement.id}`} className="text-action">
                Ver liquidación
              </Link>
            </div>
          </Card>
        )}

        {sale.status === "active" && !blockingSettlement && (
          <div style={{ marginTop: "var(--space-2)" }}>
            <Button
              variant="danger"
              fullWidth
              size="lg"
              onClick={() => { setVoidError(null); setVoidConfirmOpen(true); }}
              disabled={voidSale.isPending}
            >
              Anular venta
            </Button>
          </div>
        )}

        {sale.status === "voided" && (
          <Card style={{ background: "var(--surface-sunken)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
              <Chip variant="tag-danger" size="sm">
                Anulada
              </Chip>
              <strong>Venta anulada</strong>
            </div>
            {sale.voidReason && (
              <p style={{ margin: "var(--space-1) 0 0", color: "var(--text-secondary)" }}>
                Motivo: {sale.voidReason}
              </p>
            )}
          </Card>
        )}
      </div>

      <NetReceivedSheet
        open={netSheetTarget !== null}
        title={
          sale.payments.length > 1 && netSheetTarget
            ? `Neto recibido · ${netSheetTarget.methodName}`
            : "Neto recibido"
        }
        initialCents={netSheetTarget?.currentCents ?? null}
        onClose={() => setNetSheetTarget(null)}
        onSubmit={async (cents, moneyConfirmationToken) => {
          if (!netSheetTarget) return;
          await saveNetReceived(cents, netSheetTarget.paymentId, moneyConfirmationToken);
        }}
        submitting={netSubmitting}
      />

      <Sheet
        open={voidConfirmOpen}
        onClose={() => setVoidConfirmOpen(false)}
        title="Anular venta"
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
          <p style={{ margin: 0, color: "var(--text-secondary)" }}>
            Esta acción no se puede deshacer. La venta quedará marcada como anulada y no contará en el resumen del día.
            {sale.customerId != null && " Si pertenece a una cuenta corriente, su deuda se elimina; los cobros previos pueden quedar como saldo a favor."}
          </p>
          <Input
            label="Motivo"
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            placeholder="Ej: error de carga"
          />
          {voidError && <p className="form-error" style={{ margin: 0 }}>{voidError}</p>}
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
            <Button
              variant="danger"
              fullWidth
              size="lg"
              disabled={voidSale.isPending || voidReason.trim().length < 2}
              onClick={async () => {
                setVoidError(null);
                try {
                  await voidSale.mutateAsync({ id: sale.id, reason: voidReason.trim() });
                  setVoidConfirmOpen(false);
                  navigate(backTo);
                } catch (e) {
                  // sale_in_active_settlement: alguna otra sesión liquidó un
                  // pago de esta venta entre que se abrió la pantalla y se
                  // confirmó la anulación. blockingSettlement ya cubre el
                  // caso normal (oculta el botón), esto es la red de
                  // seguridad para la condición de carrera.
                  if (e instanceof ApiError && e.body && typeof e.body === "object" && "error" in e.body && e.body.error === "sale_in_active_settlement") {
                    setVoidError("Esta venta tiene un pago ya liquidado. Revertí la liquidación primero.");
                  } else {
                    setVoidError("No se pudo anular la venta. Volvé a intentar.");
                  }
                }
              }}
            >
              {voidSale.isPending ? "Anulando..." : "Anular venta"}
            </Button>
            <Button
              variant="ghost"
              fullWidth
              onClick={() => setVoidConfirmOpen(false)}
            >
              Cancelar
            </Button>
          </div>
        </div>
      </Sheet>
    </main>
  );
}

function NetReceivedSheet({
  open,
  title,
  initialCents,
  onClose,
  onSubmit,
  submitting,
}: {
  open: boolean;
  title: string;
  // Null when the payment's net hasn't been loaded yet (backfill flow); the
  // input starts empty in that case. Otherwise prefills with the current
  // amount so editing a value that was mistyped doesn't require retyping it.
  initialCents: number | null;
  onClose: () => void;
  onSubmit: (cents: number, moneyConfirmationToken?: string) => Promise<void>;
  submitting: boolean;
}) {
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pendingConfirmation, setPendingConfirmation] = useState<{
    cents: number;
    token: string;
    amountCents: number;
  } | null>(null);

  useEffect(() => {
    if (open) {
      setValue(initialCents !== null ? inputValueFromCents(initialCents) : "");
      setError(null);
      setPendingConfirmation(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const confirm = async () => {
    setError(null);
    const parsed = parseArsInput(value);
    if (!parsed.ok) {
      setError(parsed.message);
      return;
    }
    const cents = parsed.cents;
    if (cents <= 0) {
      setError("El monto tiene que ser mayor a 0");
      return;
    }
    if (getArsAmountPolicy(cents) === "blocked") {
      setError("El importe supera el máximo permitido");
      return;
    }
    try {
      await onSubmit(cents);
      onClose();
    } catch (e) {
      const confirmation = getMoneyConfirmation(e);
      if (confirmation) {
        setPendingConfirmation({
          cents,
          token: confirmation.token,
          amountCents: confirmation.amountCents ?? cents,
        });
      } else {
        setError((e as Error).message);
      }
    }
  };

  const confirmUnusualAmount = async () => {
    if (!pendingConfirmation) return;
    setError(null);
    try {
      // Keep the original cents frozen: the confirmation token is bound to
      // this exact request body by the API and must never be reused for a
      // later edit.
      await onSubmit(pendingConfirmation.cents, pendingConfirmation.token);
      setPendingConfirmation(null);
      onClose();
    } catch (e) {
      const confirmation = getMoneyConfirmation(e);
      if (confirmation) {
        setPendingConfirmation((current) => current && {
          ...current,
          token: confirmation.token,
          amountCents: confirmation.amountCents ?? current.amountCents,
        });
      } else {
        setPendingConfirmation(null);
        setError((e as Error).message);
      }
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title={title}>
      {pendingConfirmation ? (
        <MoneyConfirmationPanel
          amountCents={pendingConfirmation.amountCents}
          actionLabel="Confirmar neto recibido"
          pending={submitting}
          onConfirm={confirmUnusualAmount}
          onCancel={() => setPendingConfirmation(null)}
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
          <MoneyInput
            aria-label="Neto recibido"
            placeholder="Neto recibido"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setError(null);
            }}
            autoFocus
            error={error ?? undefined}
          />
          <Button variant="primary" fullWidth size="lg" onClick={confirm} disabled={submitting}>
            {submitting ? "Guardando..." : "Guardar"}
          </Button>
        </div>
      )}
    </Sheet>
  );
}

function getMoneyConfirmation(error: unknown): { token: string; amountCents?: number } | null {
  if (!(error instanceof ApiError) || error.status !== 422 || !error.body || typeof error.body !== "object") return null;
  const body = error.body as {
    error?: unknown;
    confirmationToken?: unknown;
    warnings?: Array<{ path?: unknown; amountCents?: unknown }>;
  };
  if (body.error !== "money_confirmation_required" || typeof body.confirmationToken !== "string") return null;
  const amountCents =
    body.warnings?.find((warning) => warning.path === "netReceivedCents")?.amountCents
    ?? body.warnings?.[0]?.amountCents;
  return { token: body.confirmationToken, amountCents: typeof amountCents === "number" ? amountCents : undefined };
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        fontSize: "var(--font-size-14)",
      }}
    >
      <span style={{ color: "var(--text-secondary)" }}>{label}</span>
      <span className="num">{value}</span>
    </div>
  );
}

const centerStyle: React.CSSProperties = {
  minHeight: "100dvh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
